import React, {ReactElement, Suspense} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {useNavigate} from 'react-router';
import {diApi} from '@di-shared/api/di';
import useAvailableComponentKr, {SeoulMileageSupportedType} from '@di-shared/features/home/kr/useAvailableComponent.kr';
import useMileageAgreementNotice from '@di-shared/features/mileage/agreement/useMileageAgreementNotice';
import {getHomeMileageRouteCase, HomeMileageRouteCase} from '@di-shared/features/mileage/ecomileage/utils';
import {ROUTE_PATHS} from '@di-shared/routes';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import {useSuspenseQuery} from '@tanstack/react-query';

interface MileageConditionalRendererProps {
  errorFallback?: ReactElement;
  loadingFallback?: ReactElement;
  vin: string;

  ifAgree(checkAndGotoMileagePage: () => void): ReactElement;

  ifSupportedNotAgree(openMileageSingUpBottomSheet: () => void): ReactElement;

  ifNotSupported(): ReactElement;
}

{
  /**
   * [2025-04-25:Jeyoung Lee] FIXME:테스트 해봐야 하는거
   * [x] 서울시 지원x -> 미노출
   * [x] 서울시 지원o, 동의x -> 모달 뜨고나서 인트로 페이지로 이동해야함
   * [x] 서울시 지원o, 동의o, PENDING_APPROVAL -> alert 띄우기
   * [x] 서울시 지원o, 동의o, PERFORMANCE_REGISTRATION_REQUIRED -> 실적등록 페이지로 이동
   * [x] 서울시 지원o, 동의o, MILEAGE_ACCESS_ALLOWED -> 마일리지 페이지로 이동
   *
   */
}

function MileageConditionalRenderer({
  ifSupportedNotAgree,
  ifNotSupported,
  ifAgree,
  vin,
  loadingFallback = <></>,
  errorFallback = <></>,
}: MileageConditionalRendererProps): ReactElement {
  const {seoulMileageSupportedType} = useAvailableComponentKr({vin});
  const navigate = useNavigate();

  async function openMileageSingUpBottomSheet() {
    const isConfirm = await ccWebviewUtils.openBottomSheetWebViewUrl<boolean>(
      ROUTE_PATHS.BOTTOM_SHEET_MILEAGE_SIGNUP_ELIGIBILITY.getFullPath()
    );
    if (isConfirm) {
      navigate(ROUTE_PATHS.MILEAGE_INTRO.getFullPath(vin));
    }
  }

  if (seoulMileageSupportedType === SeoulMileageSupportedType.NOT_SUPPORTED) {
    return ifNotSupported();
  }

  if (seoulMileageSupportedType === SeoulMileageSupportedType.SUPPORTED_NOT_AGREE) {
    return ifSupportedNotAgree(openMileageSingUpBottomSheet);
  }

  if (seoulMileageSupportedType === SeoulMileageSupportedType.SUPPORTED_AGREE) {
    return (
      <ErrorBoundary fallback={errorFallback}>
        <Suspense fallback={loadingFallback}>
          <MileageNavigator vin={vin}>{(checkAndGotoMileagePage) => ifAgree(checkAndGotoMileagePage)}</MileageNavigator>
        </Suspense>
      </ErrorBoundary>
    );
  }

  return <></>;
}

export default MileageConditionalRenderer;

interface MileageNavigatorProps {
  vin: string;

  children(checkAndGotoMileagePage: () => void): ReactElement;
}

function MileageNavigator({vin, children}: MileageNavigatorProps) {
  const navigate = useNavigate();
  const {openMileageApprovalPendingAlert} = useMileageAgreementNotice();
  const {data} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.ecoMileageController.getInfoLink), vin],
    queryFn: () => diApi.ecoMileageController.getInfoLink({vin}),
  });

  const {confm_stt_cd: confmSttCd, drvng_rgs_yn: drvngRgsYn, season_rgs_yn: seasonRgsyn} = data.data.rtn_data;
  const homeMileageRouteCase = getHomeMileageRouteCase({confmSttCd, drvngRgsYn, seasonRgsyn});

  async function checkAndGotoMileagePage() {
    if (homeMileageRouteCase === HomeMileageRouteCase.REGISTRATION_GUIDE_2026) {
      navigate(ROUTE_PATHS.MILEAGE_PARTICIPATION_GUIDE.getFullPath(vin));
    }
    if (homeMileageRouteCase === HomeMileageRouteCase.MILEAGE_ACCESS_ALLOWED) {
      navigate(ROUTE_PATHS.MILEAGE.getFullPath(vin));
    }
    if (homeMileageRouteCase === HomeMileageRouteCase.PERFORMANCE_REGISTRATION_REQUIRED) {
      navigate(ROUTE_PATHS.MILEAGE_REGISTER.getFullPath(vin));
    }
    if (homeMileageRouteCase === HomeMileageRouteCase.PENDING_APPROVAL) {
      await openMileageApprovalPendingAlert();
    }
  }

  return children(checkAndGotoMileagePage);
}
